import React, { useState, useMemo, useRef, useEffect } from 'react';
import { Modal, Button } from 'reactstrap';
import moment from 'moment';
import './CourseExportModal.css';

/*
  CourseExportModal
  Props:
    - isOpen
    - toggle
    - classes (array) -> lista de turmas (já filtradas ou completas)
    - baseData -> dados do curso (para alunos únicos, etc.)
    - translate
    - onCollectParticipants(classId): função async opcional para garantir participantes carregados
    - classParticipants: { [classId]: [users] }
*/

const CLASS_FIELDS = [
  { key: 'id', label: 'ID Turma' },
  { key: 'name', label: 'Nome Turma' },
  { key: 'openingDate', label: 'Abertura' },
  { key: 'closingDate', label: 'Encerramento' },
  { key: 'companyName', label: 'Unidade' },
  { key: 'students', label: 'Alunos (derivado)' },
  { key: 'teachers', label: 'Instrutores' },
  { key: 'admins', label: 'Admins' },
  { key: 'participants', label: 'Participantes' },
  { key: 'activeNonBlocked', label: 'Não-bloqueados (countActive)' },
  { key: 'blocked', label: 'Bloqueados (countInactive)' },
  { key: 'unitsCount', label: 'Qtd Units' },
  { key: 'activeStatus', label: 'Status Ativa (datas)' },
];

const PARTICIPANT_FIELDS = [
  { key: 'participantId', label: 'Participante ID' },
  { key: 'participantName', label: 'Participante Nome' },
  { key: 'participantEmail', label: 'Participante Email' },
  { key: 'participantRole', label: 'Papel' },
  { key: 'participantStatus', label: 'Status (Bloqueado?)' },
  { key: 'classId', label: 'Turma ID' },
  { key: 'className', label: 'Turma Nome' }
];

function deriveClassMetrics(c){
  const teachers = Number(c.countTeachers || 0);
  const admins = Number(c.countAdmins || 0);
  const participants = Number(c.countParticipants || 0);
  let students;
  if (c.countStudents !== undefined && c.countStudents !== null) {
    students = Math.max(0, Number(c.countStudents) || 0);
  } else if (participants) {
    students = Math.max(0, participants - teachers - admins);
  } else {
    students = 0;
  }
  if (participants > 0 && participants === teachers + admins) students = 0;
  if (participants > 0 && (students + teachers + admins) > participants) {
    students = Math.max(0, participants - teachers - admins);
  }
  const unitsCount = Array.isArray(c.units) ? c.units.length : 0;
  const openOk = c.openingDate ? moment(c.openingDate).isSameOrBefore(moment(),'day') : true;
  const closeOk = c.closingDate ? moment(c.closingDate).isSameOrAfter(moment(),'day') : true;
  const activeStatus = (openOk && closeOk) ? 'ATIVA' : 'INATIVA';
  return {
    id: c.id,
    name: c.name,
    openingDate: c.openingDate ? moment(c.openingDate).format('YYYY-MM-DD') : '',
    closingDate: c.closingDate ? moment(c.closingDate).format('YYYY-MM-DD') : '',
    companyName: c.companyName || c.company || '',
    students,
    teachers,
    admins,
    participants,
    activeNonBlocked: Number(c.countActive || 0),
    blocked: Number(c.countInactive || 0),
    unitsCount,
    activeStatus
  };
}

function normRole(user){
  if(!user) return '';
  const raw = (user.classRole !== undefined && user.classRole !== null) ? user.classRole : user.accessLevel;
  if (raw === undefined || raw === null) return '';
  const s = String(raw).toLowerCase();
  if (s === '6' || s === 'admin' || s === 'administrator') return 'admin';
  if (s === '3' || s === 'teacher' || s === 'instrutor' || s === 'instructor') return 'teacher';
  if (s === '4' || s === 'student' || s === 'aluno') return 'student';
  return '';
}

function statusLabel(u){
  const blocked = Number(u?.blocked || 0);
  return (blocked === 1 || blocked === -1 || u?.blocked === true) ? 'Bloqueado' : 'Não bloqueado';
}

// Gera CSV com opções de BOM, final de linha, aspas e linha "sep=" para Excel detectar delimitador
function buildCsv(rows, { delimiter=';', bom=true, crlf=true, alwaysQuote=false, excelSepLine=true }){
  const D = delimiter;
  const EOL = crlf ? '\r\n' : '\n';
  const escape = (v) => {
    if (v === null || v === undefined) return '';
    let s = String(v).replace(/\r?\n|\r/g,' ').replace(/\s+/g,' ').trim();
    // Excel melhor interpreta separador ; em PT-BR, mas oferecemos escolha
    const needQuote = alwaysQuote || /["\n\r,;\t]/.test(s) || s.startsWith(' ') || s.endsWith(' ');
    if (needQuote) s = '"' + s.replace(/"/g,'""') + '"';
    return s;
  };
  const body = rows.map(r => r.map(escape).join(D)).join(EOL);
  const sepPrefix = excelSepLine ? `sep=${D}` + EOL : '';
  return (bom ? '\uFEFF' : '') + sepPrefix + body;
}

function downloadCsv(content, fileName){
  const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export default function CourseExportModal({ isOpen, toggle, classes = [], baseData, translate = {}, onCollectParticipants, classParticipants = {} }) {
  const [selectedClassFields, setSelectedClassFields] = useState(() => CLASS_FIELDS.map(f=>f.key));
  const [includeParticipants, setIncludeParticipants] = useState(false);
  const [selectedParticipantFields, setSelectedParticipantFields] = useState(() => PARTICIPANT_FIELDS.map(f=>f.key));
  const [onlyFiltered, setOnlyFiltered] = useState(true);
  const [loadingGen, setLoadingGen] = useState(false);
  const [error, setError] = useState(null);
  const [delimiter, setDelimiter] = useState(';');
  const [includeBom, setIncludeBom] = useState(true);
  const [useLabels, setUseLabels] = useState(true);
  const [alwaysQuote, setAlwaysQuote] = useState(false);
  const [crlf, setCrlf] = useState(true);
  const [excelSepLine, setExcelSepLine] = useState(true); 
  const classParticipantsRef = useRef(classParticipants || {});

  useEffect(() => {
    classParticipantsRef.current = classParticipants || {};
  }, [classParticipants]);

  // Ordenar turmas por data de abertura e nome para organização
  const sortedClasses = useMemo(() => {
    return [...classes].sort((a,b)=>{
      const da = a.openingDate || ''; const db = b.openingDate || '';
      if (da === db) return (a.name||'').localeCompare(b.name||'');
      return String(da).localeCompare(String(db));
    });
  }, [classes]);

  const toggleClassField = (k) => setSelectedClassFields(prev => prev.includes(k) ? prev.filter(x=>x!==k) : [...prev,k]);
  const toggleParticipantField = (k) => setSelectedParticipantFields(prev => prev.includes(k) ? prev.filter(x=>x!==k) : [...prev,k]);

  const canGenerate = selectedClassFields.length > 0 || (includeParticipants && selectedParticipantFields.length > 0);

  const delay = (ms = 80) => new Promise(resolve => setTimeout(resolve, ms));

  const ensureParticipantsForClass = async (classId) => {
    let list = classParticipantsRef.current?.[classId];
    if (Array.isArray(list)) {
      return list;
    }

    if (onCollectParticipants) {
      try {
        const ret = onCollectParticipants(classId);
        if (ret && ret.then) {
          const awaited = await ret;
          if (Array.isArray(awaited)) {
            list = awaited;
          }
        } else if (Array.isArray(ret)) {
          list = ret;
        }
      } catch (err) {
        console.error('Erro ao buscar participantes para exportação:', err);
      }
    }

    if (!Array.isArray(list)) {
      for (let attempt = 0; attempt < 8; attempt++) {
        await delay(attempt < 4 ? 80 : 160);
        const current = classParticipantsRef.current?.[classId];
        if (Array.isArray(current)) {
          list = current;
          break;
        }
      }
    }

    if (Array.isArray(list)) {
      classParticipantsRef.current = { ...classParticipantsRef.current, [classId]: list };
      return list;
    }

    return [];
  };

  const generate = async () => {
    setError(null);
    setLoadingGen(true);
    try {
      // Garantir participantes se necessários
      const headerKeys = [];
      headerKeys.push(...selectedClassFields);
      if (includeParticipants) headerKeys.push(...selectedParticipantFields);

      // Cabeçalho amigável opcional
      const labelMap = {};
      CLASS_FIELDS.forEach(f => labelMap[f.key] = f.label);
      PARTICIPANT_FIELDS.forEach(f => labelMap[f.key] = f.label);
      const headerRow = useLabels ? headerKeys.map(k => labelMap[k] || k) : headerKeys;
      const rows = [headerRow];

      for (const c of sortedClasses) {
        const metrics = deriveClassMetrics(c);
        const participantsList = includeParticipants ? await ensureParticipantsForClass(c.id) : [];
        if (!includeParticipants || participantsList.length === 0) {
          rows.push(headerKeys.map(h => metrics[h] !== undefined ? metrics[h] : ''));
        } else {
          participantsList.forEach(u => {
            const pData = {
              participantId: u.id,
              participantName: (u.name||'') + ' ' + (u.lastName||''),
              participantEmail: u.email || '',
              participantRole: normRole(u),
              participantStatus: statusLabel(u),
              classId: c.id,
              className: c.name
            };
            rows.push(headerKeys.map(h => {
              if (metrics[h] !== undefined) return metrics[h];
              if (pData[h] !== undefined) return pData[h];
              return '';
            }));
          });
        }
      }
  const csvContent = buildCsv(rows, { delimiter, bom: includeBom, crlf, alwaysQuote, excelSepLine });
      downloadCsv(csvContent, 'curso_export_' + moment().format('YYYYMMDD_HHmmss') + '.csv');
      toggle();
    } catch(e){
      console.error(e);
      setError('Erro ao gerar CSV.');
    } finally {
      setLoadingGen(false);
    }
  };

  const translateOr = (k, d) => translate[k] || d;

  return (
    <Modal
      className="modal-dialog-centered"
      isOpen={isOpen}
      toggle={toggle}
      size="lg"
      contentClassName="course-export-modal"
    >
      <div className="modal-header d-flex align-items-center">
        <h5 className="modal-title mb-0 text-white"><i className="fas fa-file-csv mr-2"></i>{translateOr('exportar_csv','Exportar CSV')}</h5>
        <button className="close text-white" onClick={toggle} type="button"><span>×</span></button>
      </div>
      <div className="modal-body">
        <p className="modal-intro text-muted">{translateOr('selecione_informacoes_exportar','Selecione as informações que deseja exportar:')}</p>

        <div className="config-card shadow-sm">
          <div className="config-row">
            <div className="config-format">
              <label className="section-label">{translateOr('formato','Formato')}</label>
              <select className="form-control form-control-sm brand-input" value={delimiter} onChange={e=>setDelimiter(e.target.value)}>
                <option value=";">; (Excel PT-BR)</option>
                <option value=",">, (Padrão internacional)</option>
                <option value="\t">TAB</option>
              </select>
            </div>
            <div className="config-flags">
              <label className="flag-chip" title="Insere linha 'sep=' para o Excel reconhecer o delimitador automaticamente">
                <input type="checkbox" checked={excelSepLine} onChange={e=>setExcelSepLine(e.target.checked)} />sep= (Excel)
              </label>
              <label className="flag-chip">
                <input type="checkbox" checked={useLabels} onChange={e=>setUseLabels(e.target.checked)} />{translateOr('usar_cabecalhos_legiveis','Cabeçalhos legíveis')}
              </label>
              <label className="flag-chip">
                <input type="checkbox" checked={alwaysQuote} onChange={e=>setAlwaysQuote(e.target.checked)} />{translateOr('sempre_aspas','Sempre usar aspas')}
              </label>
              <label className="flag-chip" title="Inclui BOM UTF-8 no início do arquivo">
                <input type="checkbox" checked={includeBom} onChange={e=>setIncludeBom(e.target.checked)} />BOM (UTF-8)
              </label>
              <label className="flag-chip" title="Usa CRLF (Windows) em vez de LF">
                <input type="checkbox" checked={crlf} onChange={e=>setCrlf(e.target.checked)} />CRLF (Windows)
              </label>
            </div>
          </div>
        </div>
        <div className="fields-section">
          <div className="section-header">
            <h6 className="section-title">{translateOr('campos_turma','Campos de Turma')}</h6>
            <span className="section-hint text-muted">{translateOr('selecione_campos_turma','Escolha quais colunas exportar para cada turma.')}</span>
          </div>
          <div className="field-grid">
            {CLASS_FIELDS.map(f => (
              <label key={f.key} className="field-toggle">
                <input type="checkbox" checked={selectedClassFields.includes(f.key)} onChange={()=>toggleClassField(f.key)} />
                <span>{f.label}</span>
              </label>
            ))}
          </div>
        </div>
        <div className="participants-section">
          <label className="participants-toggle">
            <input type="checkbox" checked={includeParticipants} onChange={e=>setIncludeParticipants(e.target.checked)} />
            <span>{translateOr('incluir_participantes_detalhe','Incluir linhas de participantes (uma linha por participante).')}</span>
          </label>
          {includeParticipants && (
            <div className="participant-fields">
              <h6 className="section-title">{translateOr('campos_participantes','Campos de Participantes')}</h6>
              <div className="field-grid">
                {PARTICIPANT_FIELDS.map(f => (
                  <label key={f.key} className="field-toggle">
                    <input type="checkbox" checked={selectedParticipantFields.includes(f.key)} onChange={()=>toggleParticipantField(f.key)} />
                    <span>{f.label}</span>
                  </label>
                ))}
              </div>
              <small className="text-muted d-block mt-2 note-text">{translateOr('aviso_participantes_carregados','Somente participantes já carregados aparecerão; abrimos automaticamente os que faltarem.')}</small>
            </div>
          )}
        </div>
        {error && <div className="alert alert-danger py-2 mt-3 mb-0" style={{fontSize:'.75rem'}}>{error}</div>}
      </div>
      <div className="modal-footer d-flex justify-content-between align-items-center w-100">
        <Button color="primary" outline size="sm" className="footer-btn" onClick={toggle}>{translateOr('cancelar','Cancelar')}</Button>
        <Button
          color="primary"
          size="sm"
          className="footer-btn"
          disabled={!canGenerate || loadingGen}
          onClick={generate}
        >
          {loadingGen ? <><i className="fas fa-spinner fa-spin mr-1"/> {translateOr('gerando','Gerando...')}</> : <><i className="fas fa-file-export mr-1"/> {translateOr('gerar_csv','Gerar CSV')}</>}
        </Button>
      </div>
    </Modal>
  );
}
